import { computed } from "vue";
import { fabric } from "fabric";
import random from "lodash/random";

import { store } from "@/store";

import fontData from "@/data/fonts.json";

import { CurrentLabel, SheetLabel } from "@/types";

const currentLabel = computed<CurrentLabel>(() => store.getters.currentLabel);
const currentLabelType = computed<string>(() => store.getters.currentLabelType);

const browserSVGId = "without-bleed";
const adobeSVGId = "with-bleed";
const browserToAdobeDPI = 72 / 96;

const getPosition = (pos?: number): number => {
  if (pos !== undefined) {
    return pos;
  } else {
    throw new Error("Missing pos");
  }
};
const getScaledDims = (size?: number, scale?: number): number => {
  if (size !== undefined && scale !== undefined) {
    return size * scale;
  } else {
    throw new Error("Missing size or scale");
  }
};

export const prepareArtwork = async (group: fabric.Group, isRotated: boolean): Promise<string> => {
  const labelSizeWithBleed = group.getObjects().find((obj) => obj.name === "labelCanvas");
  const labelSize = group.getObjects().find((obj) => obj.name === "actualLabelSize");

  if (labelSizeWithBleed && labelSize) {
    labelSize.set({
      svgStylesToExclude: {
        stroke: true,
        strokeWidth: true,
      },
    });

    return adjustGroupDimensions(isRotated, group, labelSizeWithBleed, labelSize);
  } else {
    throw new Error(
      "Could not prepare artwork for save, missing properties. usePrepareSaveArtwork.ts"
    );
  }
};

const cloneGroup = (cloneGroup: fabric.Group): Promise<fabric.Group> => {
  return new Promise((resolve) => {
    cloneGroup.clone(
      function(clone: fabric.Group) {
        resolve(clone);
      },
      ["name", "type", "angle"]
    );
  });
};

const adjustGroupDimensions = async (
  isRotated: boolean,
  group: fabric.Group,
  labelSizeWithBleed: fabric.Object,
  labelSize: fabric.Object
) => {
  const groupWidth = getScaledDims(group.width, group.scaleX);
  const groupHeight = getScaledDims(group.height, group.scaleY);

  const withBleedLeft = getPosition(labelSizeWithBleed.left);
  const withBleedTop = getPosition(labelSizeWithBleed.top);
  const withBleedWidth = getScaledDims(labelSizeWithBleed.width, labelSizeWithBleed.scaleX);
  const withBleedHeight = getScaledDims(labelSizeWithBleed.height, labelSizeWithBleed.scaleY);

  let adjustX = withBleedLeft + (groupWidth - withBleedWidth) / 2;
  let adjustY = withBleedTop + (groupHeight - withBleedHeight) / 2;

  if (isRotated) {
    adjustX = withBleedLeft + (groupWidth - withBleedHeight) / 2;
    adjustY = withBleedTop + (groupHeight - withBleedWidth) / 2;
  }

  group.set({
    left: (groupWidth / 2 - adjustX) * browserToAdobeDPI,
    top: (groupHeight / 2 - adjustY) * browserToAdobeDPI,
  });

  let clonedGroup = await cloneGroup(group);

  group = await setClipPath(group, labelSizeWithBleed, isRotated);
  clonedGroup = await setClipPath(clonedGroup, labelSize, isRotated);

  group.set({
    scaleX: group.scaleX! * browserToAdobeDPI,
    scaleY: group.scaleY! * browserToAdobeDPI,
    id: adobeSVGId,
  });

  clonedGroup.set({
    scaleX: clonedGroup.scaleX! * browserToAdobeDPI,
    scaleY: clonedGroup.scaleY! * browserToAdobeDPI,
    id: browserSVGId,
  });

  const labelWidth = withBleedWidth * browserToAdobeDPI;
  const labelHeight = withBleedHeight * browserToAdobeDPI;

  if (isRotated) {
    return generateSVG(group, clonedGroup, labelHeight, labelWidth);
  } else {
    return generateSVG(group, clonedGroup, labelWidth, labelHeight);
  }
};

const generateSVG = (
  group: fabric.Group,
  clonedGroup: fabric.Group,
  labelSizeWidth: number,
  labelSizeHeight: number
): string => {
  const getFonts = (group: fabric.Group): string => {
    const fontFamiles: string[] = [];

    const groupObjects = group.getObjects();
    groupObjects.forEach((obj) => {
      if (obj.name && obj.name === "iText") {
        const text = obj as fabric.IText;
        fontFamiles.push(text.fontFamily!);
      }
    });

    const fonts = fontData as { [key: string]: any };

    let defs = "<defs><style>";

    fontFamiles
      .map((font) => {
        return fonts[font];
      })
      .forEach((font) => {
        defs += `@import url('https://fonts.googleapis.com/css?family=${font}');`;
      });

    return (defs += "</style></defs>");
  };

  //Need to randomize clip path id because when multiple artworks are loaded on the same screen(such as customer saved designs page) with same Ids the clip paths will interfere with each other

  // Because we are placing two copies of the same group in one svg, we need to hide the group that is clipped to the actual label size(to display in browser) from being seen in adobe by the designers

  //At the same time we need to hide the group that is clipped to bleed size so the users on the site so don't see it interfering when looking at the svg in the browser

  //We flip-flop when each group is being seen whether in browser or in adobe by giving the group clipped to actual label size an opacity of 0 and applying opacity: 1 !important; using css

  //We hide the group that is clipped to the bleed by targeting it's Id and giving it display: none;

  //Group clipped to actual label size = without-bleed
  //Group clipped to label size + bleed = with-bleed
  const parser = new DOMParser();
  const serializer = new XMLSerializer();
  const randomizeClipPathId = (reviver: any) => {
    const doc = parser.parseFromString(reviver, "image/svg+xml");
    const elements = doc.getElementsByTagName("g");

    for (const el of elements) {
      if (el.getAttribute("clip-path")) {
        const rand = random(0, 1000000);

        const clipPath = el.getElementsByTagName("clipPath");

        if (clipPath[0]) {
          el.setAttribute("clip-path", `url(#CLIPPATH-${rand})`);
          clipPath[0].setAttribute("id", `CLIPPATH-${rand}`);
        }
      }
    }

    if (doc.getElementsByTagName("parsererror").length) {
      return reviver;
    } else {
      return serializer.serializeToString(doc);
    }
  };

  const svg = `<?xml version="1.0" encoding="UTF-8" standalone="no" ?><!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 1.1//EN" "http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd">
  <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${labelSizeWidth} ${labelSizeHeight}" class="editor-artwork" version="1.1" xml:space="preserve">
  <desc>Created with Fabric.js 4.3.0</desc>
  ${getFonts(group)}
  ${clonedGroup.toSVG(randomizeClipPathId)}
  ${group.toSVG(randomizeClipPathId)}
  </svg>`;

  const svgAsDoc = parser.parseFromString(svg, "image/svg+xml");
  svgAsDoc.getElementById(browserSVGId)?.setAttribute("opacity", "0"); //Opacity is set to 1 with CSS to be seen in browser

  return serializer.serializeToString(svgAsDoc);
};

// prettier-ignore
const setClipPath = (group: fabric.Group, object: fabric.Object, isRotated: boolean): Promise<fabric.Group> => {
	return new Promise((resolve, reject) => {
		switch (currentLabel.value.shape_id) {
			case 3:
			case 4: {
				const label = object as fabric.Rect;

				const withBleedClip = new fabric.Rect({
          angle: isRotated ? 90 : 0,
					left: label.left,
					top: label.top,
					width: label.width,
					height: label.height,
					scaleX: label.scaleX,
					scaleY: label.scaleY,
					rx: label.rx ? label.rx : 0,
					ry: label.ry ? label.ry : 0,
					originX: "center",
					originY: "center",
				});

        group.clipPath = withBleedClip;

				resolve(group);
				break;
			}
      case 10: {
        const label = object as fabric.Circle;

				group.clipPath = new fabric.Circle({
          angle: isRotated ? 90 : 0,
					left: label.left,
					top: label.top,
					scaleX: label.scaleX,
					scaleY: label.scaleY,
					radius: (label.width as number) / 2,
					originX: "center",
					originY: "center",
				});
        
				resolve(group);
				break;
			}
			case 5: {
				if (['blank', 'printed', 'rollprinted', 'custom-rollprinted'].includes(currentLabelType.value)) {
					fabric.loadSVGFromURL(require(`@/assets/masks/mask-oval.svg`), function(results, options) {
						const clipPath = fabric.util.groupSVGElements(results, options) as fabric.Object;

						clipPath.set({
              angle: isRotated ? 90 : 0,
							left: object.left,
							top: object.top,
							width: object.width,
							height: object.height,
							scaleX: object.scaleX,
							scaleY: object.scaleY,
							originX: "center",
							originY: "center",
							fill: "#000",
						});

						group.clipPath = clipPath;
						resolve(group);
					});
				} else {
					const sheetLabel = currentLabel.value as SheetLabel;

					fabric.loadSVGFromURL(require(`@/assets/labels/${sheetLabel.mask_id}.svg`), function(results, options) {
						const clipPath = fabric.util.groupSVGElements(results, options) as fabric.Object;

						clipPath.set({
              angle: isRotated ? 90 : 0,
							left: object.left,
							top: object.top,
							width: object.width,
							height: object.height,
							scaleX: object.scaleX,
							scaleY: object.scaleY,
							originX: "center",
							originY: "center",
							fill: "#000",
						});

            group.clipPath = clipPath;
            resolve(group);
          });
        }
        break;
      }
      case 2:
      case 6:
      case 7:
      case 8: {
        const sheetLabel = currentLabel.value as SheetLabel;
        
        if (sheetLabel.mask_url) {
          fabric.loadSVGFromURL(sheetLabel.mask_url, function (results, options) {
            const clipPath = fabric.util.groupSVGElements(results, options) as fabric.Object;

            clipPath.set({
              angle: isRotated ? 90 : 0,
              left: object.left,
              top: object.top,
              width: object.width,
              height: object.height,
              scaleX: object.scaleX,
              scaleY: object.scaleY,
              originX: "center",
              originY: "center",
              fill: "#000",
            });

            group.clipPath = clipPath;
            resolve(group);
          });
        } else {
          fabric.loadSVGFromURL(require(`@/assets/labels/${sheetLabel.mask_id}.svg`), function (results, options) {
            const clipPath = fabric.util.groupSVGElements(results, options) as fabric.Object;

            clipPath.set({
              angle: isRotated ? 90 : 0,
              left: object.left,
              top: object.top,
              width: object.width,
              height: object.height,
              scaleX: object.scaleX,
              scaleY: object.scaleY,
              originX: "center",
              originY: "center",
              fill: "#000",
            });

            group.clipPath = clipPath;
            resolve(group);
          });
        }
        break;
      }
    }
  });
};
